import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  ActionType,
  ProColumns,
  ProTable,
  ParamsType,
} from "@ant-design/pro-components";
import {
  Button,
  Typography,
  message,
  Tag,
  Popover,
  Select,
  Popconfirm,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSuggestionList,
  acceptSuggestions,
  rejectSuggestions,
  downloadSounds,
  pendingSuggestions,
  deleteSuggestion,
} from "./api/SuggestionAPI";
import {
  ISuggestion,
  IAntDProTableRequest,
  IAPIResponseGetSuggestion,
  IActionCellProps,
} from "./type/SuggestionManagementType";
import { DownloadOutlined, SoundOutlined } from "@ant-design/icons";
import { FaTrash } from "react-icons/fa";
import { Space } from "antd";
import { debounce } from "lodash";
import { ReactComponent as StopIcon } from "../../assets/svg/stop-icon.svg";

const { Title } = Typography;

const AudioPlayer: React.FC<{
  url: string;
  onDownload: () => void;
  isDownloading: boolean;
}> = ({ url, onDownload, isDownloading }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
      const handleEnded = () => setIsPlaying(false);
      audioElement.addEventListener("ended", handleEnded);
      return () => {
        audioElement.removeEventListener("ended", handleEnded);
      };
    }
  }, []);

  return (
    <Space size="small">
      <Button
        icon={
          isPlaying ? (
            <StopIcon style={{ width: "12px", height: "12px" }} />
          ) : (
            <SoundOutlined />
          )
        }
        onClick={toggleAudio}
      />
      <Button
        icon={<DownloadOutlined />}
        onClick={onDownload}
        loading={isDownloading}
      />
      <audio ref={audioRef} src={url} />
    </Space>
  );
};

const SuggestionManagement: React.FC = () => {
  const actionRef = useRef<ActionType>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // parse URLSearchParams to queryParams
  const parseSearchParams = (search: string): IAntDProTableRequest => {
    const params = new URLSearchParams(search);
    return {
      params: {
        current: parseInt(params.get("page") || "1"),
        pageSize: 100,
      },
      filter: {
        status: params.get("status") || undefined,
        hanji: params.get("hanji") || undefined,
        roman: params.get("roman") || undefined,
      },
    };
  };

  // convert queryParams to URLSearchParams string
  const toSearchParams = (queryParams: IAntDProTableRequest): string => {
    const params = new URLSearchParams();

    if (queryParams.params.current) {
      params.set("page", queryParams.params.current.toString());
    }

    if (queryParams.filter) {
      Object.entries(queryParams.filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, value.toString());
        }
      });
    }

    return params.toString();
  };

  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    undefined
  );

  const [queryParams, setQueryParams] = useState<IAntDProTableRequest>(
    parseSearchParams(location.search)
  );

  useEffect(() => {
    const searchString = toSearchParams(queryParams);

    // remove '?'
    const currentSearch = location.search.substring(1);

    if (searchString !== currentSearch) {
      navigate(`${location.pathname}?${searchString}`, { replace: true });
    }
  }, [queryParams, navigate, location.pathname]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["suggestions", queryParams],
    queryFn: () => getSuggestionList(queryParams),
  });

  const rejectMutation = useMutation({
    mutationFn: rejectSuggestions,
    onSuccess: () => {
      refetch();
      message.success("Suggestion status updated successfully");
    },
    onError: (error) => {
      console.error("Error rejecting suggestion:", error);
      message.error("Failed to reject suggestion.");
    },
  });

  const acceptMutation = useMutation({
    mutationFn: acceptSuggestions,
    onSuccess: () => {
      refetch();
      message.success("Suggestion status updated successfully.");
    },
    onError: (error) => {
      console.error("Error accepting suggestion:", error);
      message.error("Failed to accept suggestion.");
    },
  });

  const pendingMutation = useMutation({
    mutationFn: pendingSuggestions,
    onSuccess: () => {
      refetch();
      message.success("Suggestion status updated successfully.");
    },
    onError: (error) => {
      console.error("Error updating suggestion:", error);
      message.error("Failed to update suggestion.");
    },
  });

  const archiveSuggestionMutation = useMutation({
    mutationFn: deleteSuggestion,
    onSuccess: () => {
      message.success("Suggestion deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["suggestions"] });
    },
    onError: () => {
      message.error("Failed to delete the suggestion");
    },
  });

  const [downloadingSuggestionId, setDownloadingSuggestionId] = useState<
    string | null
  >(null);

  const downloadMutation = useMutation({
    mutationFn: downloadSounds,
    onSuccess: ({ data, filename }, id) => {
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success("Download started. Check your downloads folder.");
      setDownloadingSuggestionId(null);

      // Clean up the object URL
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error("Error downloading voice:", error);
      message.error("Failed to initiate download.");
      setDownloadingSuggestionId(null);
    },
  });

  const handleDownloadVoice = (id: string) => {
    setDownloadingSuggestionId(id);
    downloadMutation.mutate(id);
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case "pending":
        return <Tag color="orange">Pending</Tag>;
      case "approved":
        return <Tag color="green">Approved</Tag>;
      case "rejected":
        return <Tag color="red">Rejected</Tag>;
      default:
        return <Tag color="geekblue">{status}</Tag>;
    }
  };

  const debouncedUpdateSearchParams = useCallback(
    debounce((newParams: Partial<IAntDProTableRequest>) => {
      setQueryParams((prev) => ({
        ...prev,
        ...newParams,
        params: { ...prev.params, current: 1 }, // Reset to first page on filter change
      }));
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateSearchParams.cancel();
    };
  }, [debouncedUpdateSearchParams]);

  const ActionCell: React.FC<IActionCellProps> = ({
    record,
    acceptMutation,
    pendingMutation,
    rejectMutation,
    deleteMutation,
  }) => {
    const [selectedAction, setSelectedAction] = useState(record.status);

    const isLoading =
      acceptMutation.status === "pending" ||
      pendingMutation.status === "pending" ||
      rejectMutation.status === "pending";

    useEffect(() => {
      if (!isLoading) {
        setSelectedAction(record.status);
      }
    }, [record.status, isLoading]);

    const handleApply = () => {
      const newStatus = selectedAction;
      record.status = newStatus;
      switch (selectedAction) {
        case "approved":
          acceptMutation.mutate(record._id);
          break;
        case "pending":
          pendingMutation.mutate(record._id);
          break;
        case "rejected":
          rejectMutation.mutate(record._id);
          break;
      }
    };

    const options = [
      { label: "Accept", value: "approved" },
      { label: "Pending", value: "pending" },
      { label: "Reject", value: "rejected" },
    ];

    return (
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Select
          defaultValue={selectedAction}
          onChange={setSelectedAction}
          options={options}
          style={{ width: 120 }}
          disabled={isLoading}
        />
        <Button
          type="primary"
          onClick={handleApply}
          disabled={selectedAction === record.status}
        >
          Apply
        </Button>
        <Popconfirm
          title="Are you sure you want to delete this suggestion?"
          onConfirm={() => record._id && deleteMutation.mutate(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <Button icon={<FaTrash />} />
        </Popconfirm>
      </div>
    );
  };

  const columns: ProColumns<ISuggestion>[] = [
    {
      title: "Chinese character",
      dataIndex: "hanji",
      width: "auto",
      valueType: "text",
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedUpdateSearchParams({
            filter: { ...queryParams.filter, hanji: e.target.value },
          }),
      },
    },
    {
      title: "Romanisation",
      dataIndex: "roman",
      width: "auto",
      valueType: "text",
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedUpdateSearchParams({
            filter: { ...queryParams.filter, roman: e.target.value },
          }),
      },
    },
    {
      title: "Locale",
      dataIndex: "originWord",
      width: 200,
      valueType: "text",
      search: false,
      render: (_, record) => record.originWord || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => getStatusTag(record.status),
      width: "auto",
      valueType: "select",
      valueEnum: {
        pending: { text: "Pending", status: "Warning" },
        approved: { text: "Approved", status: "Success" },
        rejected: { text: "Rejected", status: "Error" },
      },
      fieldProps: {
        value: selectedStatus,
        onChange: (value: string) => {
          setSelectedStatus(value);
          debouncedUpdateSearchParams({
            filter: { ...queryParams.filter, status: value },
          });
        },
      },
    },
    {
      title: "Recording",
      dataIndex: "recordURL",
      width: "auto",
      search: false,
      render: (_, record) =>
        record.recordURL ? (
          <AudioPlayer
            url={record.recordURL}
            onDownload={() => record._id && handleDownloadVoice(record._id)}
            isDownloading={downloadingSuggestionId === record._id}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Example(s)",
      dataIndex: "exampleChinese",
      width: 300,
      ellipsis: true,
      search: false,
      render: (_, record) => renderExamples(record.exampleChinese),
    },
    {
      title: "User",
      renderText(text, record, index, action) {
        return record?.user?.username || "-";
      },
      width: "auto",
      search: false,
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      renderText(text, record, index, action) {
        const date = new Date(record.createdAt);
        const formattedDate = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        const formattedTime = date
          .toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .toLowerCase();
        return `${formattedDate}, ${formattedTime}`;
      },
      width: "auto",
      search: false,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      search: false,
      render: (_, record) => {
        return (
          <ActionCell
            record={record}
            acceptMutation={acceptMutation}
            pendingMutation={pendingMutation}
            rejectMutation={rejectMutation}
            deleteMutation={archiveSuggestionMutation}
          />
        );
      },
      width: "auto",
      fixed: "right",
    },
  ];

  const renderExamples = (examples: any[]) => {
    if (!examples || examples.length === 0) {
      return "-";
    }

    const visibleExamples = examples.slice(0, 2);
    const remainingExamples = examples.slice(2);

    const exampleList = (
      <ol
        style={{
          margin: 0,
          paddingLeft: "20px",
          maxWidth: 300,
          textWrap: "wrap",
        }}
      >
        {visibleExamples.map((example, index) => (
          <li key={index}>
            {typeof example === "object" ? example.example : example}
          </li>
        ))}
      </ol>
    );

    if (remainingExamples.length === 0) {
      return exampleList;
    }

    const allExamples = (
      <ol style={{ margin: 0, paddingLeft: "20px" }}>
        {examples.map((example, index) => (
          <li key={index}>
            {typeof example === "object" ? example.example : example}
          </li>
        ))}
      </ol>
    );

    return (
      <>
        {exampleList}
        <Popover content={allExamples} title="All Examples">
          <a>See all ({examples.length})</a>
        </Popover>
      </>
    );
  };

  return (
    <div style={{ paddingInline: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>User Suggestions</Title>
      </div>
      <ProTable<ISuggestion>
        actionRef={actionRef}
        columns={columns}
        dataSource={data?.results}
        loading={isLoading}
        search={{
          labelWidth: "auto",
        }}
        options={false}
        onSubmit={(params) => {
          setQueryParams((prev) => ({
            ...prev,
            params: {
              current: 1,
              pageSize: 100,
            },
            filter: {
              hanji: params.hanji,
              roman: params.roman,
            },
          }));
        }}
        onReset={() => {
          setQueryParams({
            params: { current: 1, pageSize: 100 },
            filter: {},
          });
          setSelectedStatus(undefined);
        }}
        pagination={{
          total: data?.totalResults || 0,
          pageSize: 100,
          current: queryParams.params.current || 1,
          onChange: (page) => {
            setQueryParams((prev) => ({
              ...prev,
              params: {
                ...prev.params,
                current: page || 1,
                pageSize: 100,
              },
            }));
          },
          showSizeChanger: false,
        }}
        rowKey="_id"
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default SuggestionManagement;
